import { $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_CRITICAL, PASTE_COMMAND } from 'lexical';
import { useEffect } from 'react';

export function ClipboardStylePreservationPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      event => {
        // Only process clipboard events
        if (!event || !(event instanceof ClipboardEvent)) {
          return false;
        }

        // Get HTML content from clipboard
        const htmlContent = event.clipboardData?.getData('text/html');
        if (!htmlContent) {
          return false; // Let default handling occur if no HTML content
        }

        // Prevent default paste behavior
        event.preventDefault();

        // Process the content immediately
        editor.update(() => {
          const selection = $getSelection();
          if (!$isRangeSelection(selection)) {
            return;
          }

          // Parse HTML content
          const parser = new DOMParser();
          const dom = parser.parseFromString(htmlContent, 'text/html');

          // Extract content while preserving structure
          const cleanedDOM = extractContentWithStructure(dom);

          // Generate nodes while preserving formatting
          const nodes = $generateNodesFromDOM(editor, cleanedDOM);

          // Delete any selected content
          if (!selection.isCollapsed()) {
            selection.removeText();
          }

          // Insert the nodes
          selection.insertNodes(nodes);
        });

        return true; // We handled the paste
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  return null;
}

/**
 * Extracts content from pasted HTML while preserving structure (paragraphs, lists, etc.)
 */
function extractContentWithStructure(dom: Document): Document {
  // Create a new clean document
  const cleanDOM = new DOMParser().parseFromString('<html><body></body></html>', 'text/html');
  const cleanBody = cleanDOM.body;

  try {
    // First, try to find the editor content container
    const editorContent = findEditorContentContainer(dom);

    if (editorContent) {
      // If we found a specific editor container, process its contents
      processEditorContent(editorContent, cleanBody, cleanDOM);
    } else {
      // Fallback: just try to extract what seems to be content
      extractContentElements(dom.body, cleanBody, cleanDOM);
    }

    return cleanDOM;
  } catch (e) {
    console.error('Error extracting content structure:', e);
    return dom; // Fallback to original document
  }
}

/**
 * Finds the main editor content container in the DOM
 */
function findEditorContentContainer(dom: Document): Element | null {
  const body = dom.body;

  // Look for common editor containers
  const editorContainers = [
    body.querySelector('[data-lexical-editor="true"]'),
    body.querySelector('.lexical-editor'),
    body.querySelector('.editor-inner'),
    body.querySelector('[contenteditable="true"]'),
  ].filter(Boolean);

  return editorContainers[0] || null;
}

/**
 * Process editor content to preserve structure while removing unnecessary wrappers
 */
function processEditorContent(editorContent: Element, targetBody: HTMLElement, targetDoc: Document): void {
  // These are the content elements we want to preserve
  const contentElements = editorContent.querySelectorAll(
    'p, h1, h2, h3, h4, h5, h6, ul, ol, li, blockquote, pre, div.editor-paragraph',
  );

  if (contentElements.length === 0) {
    // If no specific elements found, just copy all content
    targetBody.innerHTML = editorContent.innerHTML;
    return;
  }

  // Track parent lists to properly maintain list structure
  let currentList: HTMLElement | null = null;

  for (const element of Array.from(contentElements)) {
    // Handle list items specially to maintain list structure
    if (element.tagName === 'LI') {
      // If this is a list item, find or create its parent list
      const parentListType = element.closest('ol') ? 'ol' : 'ul';

      // Create a new list if needed or continue using the current one
      if (!currentList || currentList.tagName.toLowerCase() !== parentListType) {
        currentList = targetDoc.createElement(parentListType);

        // Preserve list classes
        if (element.parentElement?.className) {
          currentList.className = element.parentElement.className;
        }

        // Copy list styles too
        copyInlineStyles(element.parentElement as Element, currentList);

        targetBody.appendChild(currentList);
      }

      // Add the list item to the current list
      const newLi = targetDoc.createElement('li');
      newLi.className = element.className;

      // Copy all inline styles
      copyInlineStyles(element, newLi);

      // Check for alignment in classes
      processTextAlignmentFromClasses(element, newLi);

      newLi.innerHTML = element.innerHTML;
      currentList.appendChild(newLi);
    }
    // Handle regular block elements (paragraphs, headings, etc.)
    else if (element.tagName !== 'UL' && element.tagName !== 'OL') {
      // Reset current list since we're no longer in a list context
      currentList = null;

      // Create and append the element
      const newElement = targetDoc.createElement(element.tagName);
      newElement.className = element.className;

      // Copy all inline styles
      copyInlineStyles(element, newElement);

      // Check for alignment in classes
      processTextAlignmentFromClasses(element, newElement);

      // Copy dir attribute for RTL text
      if (element.hasAttribute('dir')) {
        newElement.setAttribute('dir', element.getAttribute('dir') || '');
      }

      newElement.innerHTML = element.innerHTML;
      targetBody.appendChild(newElement);
    }
    // Skip UL/OL tags as we handle them through LI processing
  }
}

/**
 * Copies all inline styles from source element to target element
 */
function copyInlineStyles(source: Element, target: Element): void {
  if (source.getAttribute('style')) {
    target.setAttribute('style', source.getAttribute('style') || '');
  }
}

/**
 * Processes text alignment information from classes and applies it as inline style
 */
function processTextAlignmentFromClasses(source: Element, target: Element): void {
  // Common alignment classes to look for
  const alignmentClasses = {
    'text-center': 'center',
    'text-right': 'right',
    'text-left': 'left',
    'text-justify': 'justify',
    'has-text-align-center': 'center',
    'has-text-align-right': 'right',
    'has-text-align-left': 'left',
    'has-text-align-justify': 'justify',
    'u-text-center': 'center',
    'u-text-right': 'right',
    'u-text-left': 'left',
  };

  // Check if element already has text-align style
  const existingStyle = target.getAttribute('style') || '';
  if (existingStyle.includes('text-align:')) {
    return; // Already has alignment style
  }

  // Check for alignment classes
  const classList = source.className.split(' ');
  for (const className of classList) {
    for (const [classKey, alignValue] of Object.entries(alignmentClasses)) {
      if (className.includes(classKey)) {
        // Apply alignment as inline style using setAttribute
        const currentStyle = target.getAttribute('style') || '';
        target.setAttribute('style', `${currentStyle}${currentStyle ? '; ' : ''}text-align: ${alignValue};`);
        return;
      }
    }
  }

  // Check if element has dir attribute and is RTL
  if (source.getAttribute('dir') === 'rtl') {
    const currentStyle = target.getAttribute('style') || '';
    target.setAttribute('style', `${currentStyle}${currentStyle ? '; ' : ''}text-align: right;`);
  }

  // If the element has any attribute that implies center alignment
  if (source.hasAttribute('align') && source.getAttribute('align') === 'center') {
    const currentStyle = target.getAttribute('style') || '';
    target.setAttribute('style', `${currentStyle}${currentStyle ? '; ' : ''}text-align: center;`);
  }
}

/**
 * Fallback function to extract content elements when no specific editor container is found
 */
function extractContentElements(sourceElement: Element, targetBody: HTMLElement, targetDoc: Document): void {
  // Look for content elements directly
  const contentSelectors = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'blockquote', 'pre'];

  // First try to find direct content elements
  const directContentElements = sourceElement.querySelectorAll(contentSelectors.join(', '));

  if (directContentElements.length > 0) {
    // We found some content elements, copy them
    directContentElements.forEach(element => {
      const newElement = targetDoc.importNode(element, true);

      // Process alignment for imported nodes too
      if (element.tagName !== 'UL' && element.tagName !== 'OL') {
        processTextAlignmentFromClasses(element, newElement);
      }

      targetBody.appendChild(newElement);
    });
    return;
  }

  // If no content elements found, look for any elements with text content
  const allElements = sourceElement.querySelectorAll('*');
  let foundContent = false;

  for (const element of Array.from(allElements)) {
    const textContent = element.textContent?.trim();
    if (textContent && !element.querySelector('*')) {
      // Only consider leaf nodes with text
      // Create a paragraph for this content
      const p = targetDoc.createElement('p');

      // Copy styles and attributes
      copyInlineStyles(element, p);
      processTextAlignmentFromClasses(element, p);

      p.innerHTML = element.innerHTML;
      targetBody.appendChild(p);
      foundContent = true;
    }
  }

  // If all else fails, just copy the body content
  if (!foundContent) {
    targetBody.innerHTML = sourceElement.innerHTML;
  }
}
